import { useCallback, useEffect, useState } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import { useAuth } from '@/context/AuthContext';
import {
  fetchRoles,
  fetchUsers,
  updateUser,
  deleteUser,
  roleLabel,
  type RoleRecord,
  type UserRecord,
} from '@/services/users';
import { getPendingCommissionsByEmail } from '@/services/commissions';
import UserModal from './components/UserModal';
import RoleModal from './components/RoleModal';
import ResetPasswordModal from './components/ResetPasswordModal';
import PermissionMatrix from './components/PermissionMatrix';

type Tab = 'equipo' | 'roles';

export default function UsersPage() {
  const { profile } = useAuth();
  const isAdmin = profile?.role === 'admin';
  const [tab, setTab] = useState<Tab>('equipo');
  const [roles, setRoles] = useState<RoleRecord[]>([]);
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showUserModal, setShowUserModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState<RoleRecord | null>(null);
  const [savingUserId, setSavingUserId] = useState<string | null>(null);
  const [resetTarget, setResetTarget] = useState<UserRecord | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<UserRecord | null>(null);
  const [pendingComms, setPendingComms] = useState<{ count: number; total: number }>({ count: 0, total: 0 });
  const [deletingNow, setDeletingNow] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await fetchRoles();
      setRoles(r);
      setSelectedRole((prev) => prev ?? r[0] ?? null);
      const u = await fetchUsers(r);
      setUsers(u);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar la información.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function changeRole(userId: string, roleId: string) {
    setSavingUserId(userId);
    try {
      await updateUser(userId, { roleId });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo actualizar el rol.');
    } finally {
      setSavingUserId(null);
    }
  }

  async function toggleActive(userId: string, next: boolean) {
    setSavingUserId(userId);
    try {
      await updateUser(userId, { isActive: next });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo actualizar el estado.');
    } finally {
      setSavingUserId(null);
    }
  }

  async function requestDelete(u: UserRecord) {
    setDeleteError('');
    setDeleteTarget(u);
    setPendingComms({ count: 0, total: 0 });
    const info = await getPendingCommissionsByEmail(u.email);
    setPendingComms(info);
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeletingNow(true);
    setDeleteError('');
    try {
      await deleteUser(deleteTarget.id);
      setDeleteTarget(null);
      await load();
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : 'No se pudo eliminar el usuario.');
    } finally {
      setDeletingNow(false);
    }
  }

  const activeCount = users.filter((u) => u.isActive).length;

  return (
    <AdminLayout>
      <div className="p-4 lg:p-6 space-y-5 max-w-[1200px] mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-foreground-950">Usuarios y Roles</h1>
            <p className="text-sm text-foreground-500 mt-0.5 flex items-center flex-wrap gap-2">
              <span>Gestión del equipo comercial y permisos</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium whitespace-nowrap">
                Supabase
              </span>
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowRoleModal(true)}
              className="text-sm px-4 py-2.5 border border-background-200 text-foreground-700 rounded-lg hover:bg-background-50 transition-colors whitespace-nowrap font-medium cursor-pointer"
            >
              <i className="ri-add-line mr-1.5"></i>Nuevo rol
            </button>
            <button
              onClick={() => setShowUserModal(true)}
              className="text-sm px-4 py-2.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors whitespace-nowrap font-medium cursor-pointer"
            >
              <i className="ri-user-add-line mr-1.5"></i>Invitar usuario
            </button>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-3 bg-red-50 text-red-700 border border-red-200 rounded-xl px-4 py-3 text-sm">
            <span>{error}</span>
            <button onClick={load} className="ml-auto text-xs font-medium underline whitespace-nowrap cursor-pointer">
              Reintentar
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="inline-flex items-center bg-background-100 rounded-full p-1">
          <button
            onClick={() => setTab('equipo')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
              tab === 'equipo' ? 'bg-white text-foreground-950' : 'text-foreground-500 hover:text-foreground-700'
            }`}
          >
            Equipo ({users.length})
          </button>
          <button
            onClick={() => setTab('roles')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
              tab === 'roles' ? 'bg-white text-foreground-950' : 'text-foreground-500 hover:text-foreground-700'
            }`}
          >
            Roles y Permisos ({roles.length})
          </button>
        </div>

        {loading ? (
          <div className="text-center py-16 text-foreground-400">
            <i className="ri-loader-4-line animate-spin text-2xl mr-2"></i>Cargando...
          </div>
        ) : tab === 'equipo' ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="bg-white rounded-xl border border-background-200 p-4">
                <p className="text-xs text-foreground-500">Usuarios totales</p>
                <p className="text-xl font-bold text-foreground-950 mt-1">{users.length}</p>
              </div>
              <div className="bg-white rounded-xl border border-background-200 p-4">
                <p className="text-xs text-foreground-500">Activos</p>
                <p className="text-xl font-bold text-emerald-600 mt-1">{activeCount}</p>
              </div>
              <div className="bg-white rounded-xl border border-background-200 p-4">
                <p className="text-xs text-foreground-500">Roles definidos</p>
                <p className="text-xl font-bold text-foreground-950 mt-1">{roles.length}</p>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-background-100 text-left bg-background-50">
                      <th className="px-5 py-3 text-xs font-medium text-foreground-500">Usuario</th>
                      <th className="px-5 py-3 text-xs font-medium text-foreground-500">Rol</th>
                      <th className="px-5 py-3 text-xs font-medium text-foreground-500">Estado</th>
                      <th className="px-5 py-3 text-xs font-medium text-foreground-500">Alta</th>
                      <th className="px-5 py-3 text-xs font-medium text-foreground-500 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-secondary-500 flex items-center justify-center flex-shrink-0">
                              <span className="text-white text-xs font-bold">{(u.fullName[0] || 'U').toUpperCase()}</span>
                            </div>
                            <div className="min-w-0">
                              <p className="text-foreground-900 font-medium truncate">{u.fullName || '—'}</p>
                              <p className="text-xs text-foreground-500 truncate">{u.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-3">
                          <select
                            value={u.roleId ?? ''}
                            onChange={(e) => changeRole(u.id, e.target.value)}
                            disabled={savingUserId === u.id}
                            className="text-xs border border-background-200 rounded-lg px-2 py-1.5 bg-white text-foreground-700 focus:outline-none focus:ring-2 focus:ring-accent-300 cursor-pointer"
                          >
                            <option value="">Sin rol</option>
                            {roles.map((r) => (
                              <option key={r.id} value={r.id}>
                                {roleLabel(r)}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-5 py-3">
                          <button
                            onClick={() => toggleActive(u.id, !u.isActive)}
                            disabled={savingUserId === u.id}
                            className={`text-xs px-2.5 py-1 rounded-full font-medium whitespace-nowrap cursor-pointer transition-colors ${
                              u.isActive
                                ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                                : 'bg-background-100 text-foreground-500 hover:bg-background-200'
                            }`}
                          >
                            {u.isActive ? 'Activo' : 'Inactivo'}
                          </button>
                        </td>
                        <td className="px-5 py-3 text-xs text-foreground-500 whitespace-nowrap">
                          {u.createdAt
                            ? new Date(u.createdAt).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' })
                            : '—'}
                        </td>
                        <td className="px-5 py-3">
                          {isAdmin && (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => setResetTarget(u)}
                                title="Cambiar contraseña"
                                className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-accent-600 hover:bg-accent-50 transition-colors cursor-pointer"
                              >
                                <i className="ri-key-2-line"></i>
                              </button>
                              <button
                                onClick={() => requestDelete(u)}
                                title="Eliminar usuario"
                                className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                              >
                                <i className="ri-delete-bin-line"></i>
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {users.length === 0 && (
                <div className="text-center py-12 text-foreground-500">
                  <i className="ri-team-line text-3xl block mb-2"></i>
                  <p className="text-sm">Aún no hay usuarios registrados. Invita al primer miembro del equipo.</p>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
            <div className="lg:col-span-1 bg-white rounded-xl border border-background-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-background-100">
                <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">Roles</p>
              </div>
              <div className="divide-y divide-background-100">
                {roles.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => setSelectedRole(r)}
                    className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors cursor-pointer ${
                      selectedRole?.id === r.id ? 'bg-accent-50' : 'hover:bg-background-50'
                    }`}
                  >
                    <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-shield-user-line text-accent-600"></i>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground-900 truncate">{roleLabel(r)}</p>
                      <p className="text-xs text-foreground-500 truncate">{r.name}</p>
                    </div>
                  </button>
                ))}
                {roles.length === 0 && (
                  <div className="px-4 py-8 text-center text-foreground-400 text-sm">
                    No hay roles. Crea uno para empezar.
                  </div>
                )}
              </div>
            </div>
            <div className="lg:col-span-2">
              {selectedRole ? (
                <PermissionMatrix
                  key={selectedRole.id}
                  role={selectedRole}
                  onSaved={load}
                />
              ) : (
                <div className="bg-white rounded-xl border border-background-200 p-10 text-center text-foreground-500">
                  <i className="ri-shield-star-line text-3xl block mb-2"></i>
                  <p className="text-sm">Selecciona un rol para editar sus permisos.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {showUserModal && (
          <UserModal roles={roles} onClose={() => setShowUserModal(false)} onCreated={() => { setShowUserModal(false); load(); }} />
        )}
        {showRoleModal && (
          <RoleModal onClose={() => setShowRoleModal(false)} onCreated={() => { setShowRoleModal(false); load(); }} />
        )}
        {resetTarget && (
          <ResetPasswordModal
            userId={resetTarget.id}
            userName={resetTarget.fullName}
            userEmail={resetTarget.email}
            onClose={() => setResetTarget(null)}
          />
        )}

        {deleteTarget && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40" onClick={() => !deletingNow && setDeleteTarget(null)}></div>
            <div className="relative w-full max-w-md bg-white rounded-xl border border-background-200 p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-base font-semibold text-foreground-950">Eliminar usuario</h3>
                  <p className="text-sm text-foreground-500 mt-0.5">{deleteTarget.email}</p>
                </div>
                <button
                  onClick={() => !deletingNow && setDeleteTarget(null)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>

              {deleteError && (
                <div className="mb-4 flex items-start gap-2 text-sm bg-red-50 text-red-700 border border-red-200 rounded-md px-3 py-2.5">
                  <i className="ri-error-warning-line mt-0.5"></i>
                  <span>{deleteError}</span>
                </div>
              )}

              {pendingComms.count > 0 ? (
                <div className="mb-4 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <i className="ri-alert-line text-amber-600 text-lg mt-0.5"></i>
                  <p className="text-sm text-amber-800">
                    Este usuario está vinculado a un vendedor con{' '}
                    <strong>{pendingComms.count} comisión(es) pendiente(s)</strong> por un total de{' '}
                    <strong>${pendingComms.total.toLocaleString()}</strong>. Si lo eliminas, se perderá la
                    relación con esas comisiones.
                  </p>
                </div>
              ) : (
                <p className="mb-4 text-sm text-foreground-600">
                  Esta acción eliminará la cuenta y su acceso al panel de forma permanente.
                </p>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => !deletingNow && setDeleteTarget(null)}
                  className="flex-1 px-4 py-2.5 text-sm border border-background-300 text-foreground-700 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={confirmDelete}
                  disabled={deletingNow}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {deletingNow ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-1.5"></i>Eliminando...
                    </>
                  ) : (
                    <>
                      <i className="ri-delete-bin-line mr-1.5"></i>Eliminar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}